import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const KEY = "ustore_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items, loaded]);

  /**
   * Tambah produk ke keranjang.
   *
   * ``orderFields`` menampung data tujuan topup (User ID / Zone ID) untuk
   * produk instan yang diteruskan ke undeadshopp.com. Kalau item yang sama
   * ditambahkan lagi dengan ID berbeda, ID terbaru yang dipakai supaya
   * diamond tidak terkirim ke akun lama.
   */
  const add = (product, quantity = 1, orderFields = null) => {
    setItems((prev) => {
      const existing = prev.find((it) => it.product_id === product.id);
      if (existing) {
        return prev.map((it) =>
          it.product_id === product.id
            ? {
                ...it,
                quantity: it.quantity + quantity,
                order_fields: orderFields || it.order_fields || null,
              }
            : it,
        );
      }

      const flash = product.flash_sale || null;
      return [
        ...prev,
        {
          product_id: product.id,
          title: product.title,
          // Harga yang dipakai untuk estimasi total. Kalau produk sedang flash
          // sale, pakai harga flash-nya. Server tetap menghitung ulang harga
          // final saat checkout, jadi ini murni untuk tampilan.
          price: flash ? flash.flash_price : product.price,
          normal_price: product.price,
          flash_sale_id: flash ? flash.id : null,
          image_url: product.image_url,
          seller_name: product.seller_name,
          seller_id: product.seller_id,
          // Sumber produk: "undeadshopp" berarti butuh User ID game.
          source: product.source || null,
          order_fields: orderFields || null,
          quantity,
        },
      ];

    });
  };

  const remove = (productId) => setItems((prev) => prev.filter((it) => it.product_id !== productId));
  const updateQty = (productId, quantity) =>
    setItems((prev) => prev.map((it) => (it.product_id === productId ? { ...it, quantity: Math.max(1, quantity) } : it)));
  /**
   * Sinkronkan ulang harga item keranjang dengan data produk terbaru.
   *
   * Dipakai halaman Cart supaya item yang flash sale-nya sudah berakhir atau
   * kehabisan stok otomatis balik ke harga normal sebelum buyer checkout.
   */
  const syncPrice = (productId, product) =>
    setItems((prev) =>
      prev.map((it) => {
        if (it.product_id !== productId) return it;
        const flash = product?.flash_sale || null;
        return {
          ...it,
          price: flash ? flash.flash_price : product.price,
          normal_price: product.price,
          flash_sale_id: flash ? flash.id : null,
        };
      }),
    );
  const clear = () => setItems([]);

  const total = items.reduce((s, it) => s + it.price * it.quantity, 0);
  const count = items.reduce((s, it) => s + it.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, updateQty, syncPrice, clear, total, count }}>

      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
